import type {
  Unsubscribe} from "firebase/firestore";
import {
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { ref, computed, onUnmounted, watch } from "vue";
import { caseConverter } from "~~/converters/case-converter";
import type { ICase} from "~~/types/case";
import { EStatus } from "~~/types/case";
import { EAccountType } from "~~/types/user";
import { useEtaSound } from "./use-eta-sound";

export const useEtaReminders = () => {
  const db = getFirestore();
  const userStore = useUserStore();
  const { startRepeatingSound, stopRepeatingSound } = useEtaSound();

  // State
  const allCasesWithEta = ref<ICase[]>([]); // Store ALL cases
  const isListening = ref(false);
  const sessionStartTime = ref<Date>(new Date());
  const shownCaseIds = ref<Set<string>>(new Set());
  const checkInterval = ref<NodeJS.Timeout | null>(null);

  let unsubscribe: Unsubscribe | null = null;

  // Check if user is admin or owner
  const isAdmin = computed(() => {
    const user = userStore.currentUser;
    return user?.accountType === EAccountType.ADMIN || user?.accountType === EAccountType.OWNER;
  });

  const casesWithEtaReached = computed(() => {
    return allCasesWithEta.value.filter((caseItem) => {
      const eta = caseItem.statusTimestamp?.estimatedArrivalAt;
      return eta && isEtaReached(eta);
    });
  });

  // Check if a case's ETA has been reached
  const isEtaReached = (etaTimestamp: any): boolean => {
    if (!etaTimestamp) return false;

    const now = new Date();
    const etaDate = etaTimestamp instanceof Timestamp ? etaTimestamp.toDate() : new Date(etaTimestamp);

    // ETA is reached only when the time has passed
    return etaDate <= now;
  };

  // Get the first unshown case with ETA reached
  const currentNotification = computed(() => {
    const unshown = casesWithEtaReached.value.find((caseItem) => !shownCaseIds.value.has(caseItem.id));
    return unshown || null;
  });

  // Check if there are more notifications to show
  const hasMoreNotifications = computed(() => {
    return casesWithEtaReached.value.some((caseItem) => !shownCaseIds.value.has(caseItem.id));
  });

  // Count of unshown notifications
  const unshownCount = computed(() => casesWithEtaReached.value.filter((c) => !shownCaseIds.value.has(c.id)).length);

  // Watch unshown count to manage repeating sound
  watch(unshownCount, (count) => {
    if (count > 0) {
      startRepeatingSound();
    } else {
      stopRepeatingSound();
    }
  });

  // Periodic check for ETA updates
  const checkForEtaUpdates = () => {
    // Force re-evaluation of computed property by triggering reactivity
    allCasesWithEta.value = [...allCasesWithEta.value];
  };

  // Start listening for cases with ETA
  const startListening = () => {
    if (!isAdmin.value) {
      console.log("Not an admin user, skipping ETA reminders");
      return;
    }

    if (isListening.value) {
      console.log("Already listening for ETA reminders");
      return;
    }

    // Set session start time to now
    sessionStartTime.value = new Date();

    try {
      // Query for cases with specific statuses, ETA tracking enabled, and not yet acknowledged
      const q = query(
        collection(db, "cases").withConverter(caseConverter),
        where("status", "in", [EStatus.ASSIGNED, EStatus.ACCEPTED, EStatus.ONGOING]),
        where("statusTimestamp.estimatedArrivalAt", "!=", null),
        where("statusTimestamp.etaReminderAcknowledgedAt", "==", null),
        orderBy("statusTimestamp.estimatedArrivalAt", "asc"),
      );

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          allCasesWithEta.value = snapshot.docs
            .map((doc) => doc.data() as ICase)
            .filter((c) => c.statusTimestamp?.estimatedArrivalAt);

          isListening.value = true;
        },
        (error) => {
          console.error("Error listening for ETA reminders:", error);
          isListening.value = false;
        },
      );

      // Set up periodic check every minute to catch ETAs that become reached
      checkInterval.value = setInterval(checkForEtaUpdates, 60 * 1000); // Check every minute
    } catch (error) {
      console.error("Error setting up ETA reminders:", error);
      isListening.value = false;
    }
  };

  // Mark current notification as shown
  const markAsShown = (caseId: string) => {
    shownCaseIds.value.add(caseId);
  };

  // Dismiss current notification
  const dismissCurrent = () => {
    if (currentNotification.value) {
      markAsShown(currentNotification.value.id);
    }
  };

  // Stop listening
  const stopListening = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }

    if (checkInterval.value) {
      clearInterval(checkInterval.value);
      checkInterval.value = null;
    }

    stopRepeatingSound();
    isListening.value = false;
  };

  // Reset all state
  const reset = () => {
    stopListening();
    stopRepeatingSound();
    allCasesWithEta.value = [];
    shownCaseIds.value.clear();
    sessionStartTime.value = new Date();
  };

  // Auto cleanup on unmount
  onUnmounted(() => {
    stopListening();
  });

  return {
    // State
    isListening,
    currentNotification,
    hasMoreNotifications,
    isAdmin,
    casesCount: computed(() => allCasesWithEta.value.length),
    unshownCount,

    // Methods
    startListening,
    stopListening,
    dismissCurrent,
    markAsShown,
    reset,
  };
};
